import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Button, Space, message, Modal, Statistic, Tooltip } from 'antd';
import { SaveOutlined, EyeOutlined, DownloadOutlined, FullscreenOutlined, FullscreenExitOutlined } from '@ant-design/icons';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

import communityApi from '../../api/community';
import { countWordsInHtml } from '../../utils/readingTime';

const quillModules = {
  toolbar: {
    container: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'indent': '-1' }, { 'indent': '+1' }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      ['link', 'image', 'video'],
      ['clean']
    ],
    handlers: {
      image: function () {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/*');
        input.click();
        input.onchange = async () => {
          const file = input.files[0];
          if (file) {
            // Provide immediate feedback by using a loading message
            const hideLoading = message.loading('Uploading image...', 0);
            try {
              const res = await communityApi.posts.uploadImage(file);
              const url = res.data.url;
              const range = this.quill.getSelection();
              this.quill.insertEmbed(range.index, 'image', url);
              hideLoading();
            } catch (error) {
              hideLoading();
              message.error('Failed to upload image. Please try again.');
              console.error('Image upload error:', error);
            }
          }
        };
      }
    }
  },
  clipboard: {
    matchVisual: false,
  },
};

const quillFormats = [
  'header',
  'bold', 'italic', 'underline', 'strike', 'blockquote',
  'list', 'indent',
  'link', 'image', 'video',
  'color', 'background', 'align'
];

const RichTextEditor = ({ form, isDarkMode, fieldName, value, onChange, placeholder, onSave }) => {
  const quillRef = useRef(null);
  const [editorValue, setEditorValue] = useState(value || '');
  const [previewVisible, setPreviewVisible] = useState(false);
  const [wordCount, setWordCount] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);

  useEffect(() => {
    setEditorValue(value || '');
    updateWordCount(value || '');
  }, [value]);

  const updateWordCount = (content) => {
    setWordCount(countWordsInHtml(content));
  };

  const quillStyle = {
    minHeight: '280px',
  };

  const handleChange = (content) => {
    setEditorValue(content);
    updateWordCount(content);
    if (onChange) {
      onChange(content);
    }
  };

  const handleSave = () => {
    if (onSave) {
      onSave(editorValue);
    }
    message.success('Content saved successfully!');
  };

  const handlePreview = () => {
    setPreviewVisible(true);
  };

  const handleExport = () => {
    const blob = new Blob([editorValue], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `content-${Date.now()}.html`;
    a.click();
    URL.revokeObjectURL(url);
    message.success('Content exported successfully!');
  };

  const readingTime = useMemo(() => {
    // ~200 words per minute
    const minutes = Math.max(1, Math.ceil(wordCount / 200));
    return `${minutes} min read`;
  }, [wordCount]);

  return (
    <div style={{ height: '100%' }} className={fullscreen ? 'fixed inset-0 z-[1000] bg-black/60 p-6 flex items-center justify-center' : ''}>
      <div className={fullscreen ? 'bg-white dark:bg-[#161616] rounded-2xl shadow-2xl w-full max-w-6xl p-4' : ''}>
        <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleSave}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-primary hover:bg-[#229bb8] text-white text-sm font-medium transition-colors"
            >
              <SaveOutlined /> Save
            </button>
            <button
              type="button"
              onClick={handlePreview}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#161616] text-gray-700 dark:text-gray-200 text-sm font-medium hover:bg-gray-50 dark:hover:bg-[#1F1F1F] transition-colors"
            >
              <EyeOutlined /> Preview
            </button>
            <button
              type="button"
              onClick={handleExport}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#161616] text-gray-700 dark:text-gray-200 text-sm font-medium hover:bg-gray-50 dark:hover:bg-[#1F1F1F] transition-colors"
            >
              <DownloadOutlined /> Export HTML
            </button>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-xs text-gray-500 dark:text-gray-400 text-right">
              <div className="font-medium">Words: {wordCount}</div>
              <div className="opacity-80">{readingTime}</div>
            </div>
            <Tooltip title={fullscreen ? 'Exit Fullscreen' : 'Fullscreen'}>
              <button
                type="button"
                onClick={() => setFullscreen(v => !v)}
                className="p-1.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#161616] text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-[#1F1F1F] transition-colors"
              >
                {fullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
              </button>
            </Tooltip>
          </div>
        </div>
        <style>
          {`
          .quill {
            display: flex;
            flex-direction: column;
            height: 100%;
            border-radius: 12px;
            border: 1px solid ${isDarkMode ? '#27272a' : '#e5e7eb'};
            overflow: hidden;
          }
          .quill .ql-container {
            flex: 1;
            height: auto;
            max-height: 560px;
            overflow-y: auto;
            border: none !important;
          }
          .quill .ql-editor {
            min-height: 280px;
            max-height: 560px;
            overflow-y: auto;
            font-size: 16px;
            line-height: 1.7;
            padding: 18px;
          }
          .quill .ql-toolbar.ql-snow {
            position: sticky;
            top: 0;
            z-index: 1;
            background: ${isDarkMode ? '#161616' : '#fafafa'};
            border: none !important;
            border-bottom: 1px solid ${isDarkMode ? '#27272a' : '#e5e7eb'} !important;
            box-shadow: ${isDarkMode ? '0 1px 4px rgba(0,0,0,0.4)' : '0 1px 4px rgba(0,0,0,0.06)'};
            padding: 8px 12px;
          }
          .quill .ql-toolbar .ql-formats {
            margin-right: 12px;
          }
          .quill .ql-editor {
            background-color: ${isDarkMode ? '#0E0E0E' : '#ffffff'};
            color: ${isDarkMode ? '#e4e4e7' : '#111827'};
          }
          .quill .ql-editor.ql-blank::before {
            color: ${isDarkMode ? '#52525b' : '#9ca3af'};
            font-style: italic;
          }
          .ql-snow .ql-tooltip {
            background-color: ${isDarkMode ? '#161616' : '#ffffff'};
            color: ${isDarkMode ? '#e4e4e7' : '#111827'};
            border-color: ${isDarkMode ? '#27272a' : '#e5e7eb'};
            box-shadow: 0 4px 12px rgba(0,0,0,${isDarkMode ? '0.5' : '0.1'});
          }
          .ql-snow .ql-tooltip input[type=text] {
            background-color: ${isDarkMode ? '#0E0E0E' : '#ffffff'};
            color: ${isDarkMode ? '#e4e4e7' : '#111827'};
            border-color: ${isDarkMode ? '#27272a' : '#e5e7eb'};
          }
          .ql-snow .ql-picker {
            color: ${isDarkMode ? '#a1a1aa' : '#374151'};
          }
          .ql-snow .ql-picker-options {
            background-color: ${isDarkMode ? '#161616' : '#ffffff'};
            border-color: ${isDarkMode ? '#27272a' : '#e5e7eb'};
          }
          .ql-snow .ql-picker-item {
            color: ${isDarkMode ? '#a1a1aa' : '#374151'};
          }
          .ql-snow .ql-picker-item:hover {
            color: ${isDarkMode ? '#ffffff' : '#111827'};
          }
          .ql-snow .ql-stroke {
            stroke: ${isDarkMode ? '#a1a1aa' : '#374151'};
          }
          .ql-snow .ql-fill {
            fill: ${isDarkMode ? '#a1a1aa' : '#374151'};
          }
          .ql-snow button:hover .ql-stroke,
          .ql-snow .ql-picker-label:hover .ql-stroke {
            stroke: ${isDarkMode ? '#ffffff' : '#111827'} !important;
          }
          .ql-snow button:hover .ql-fill,
          .ql-snow .ql-picker-label:hover .ql-fill {
            fill: ${isDarkMode ? '#ffffff' : '#111827'} !important;
          }
          .ql-snow button.ql-active .ql-stroke {
            stroke: #2Ba8CB !important;
          }
          .ql-snow button.ql-active .ql-fill {
            fill: #2Ba8CB !important;
          }
          .ql-snow .ql-picker-label.ql-active {
            color: #2Ba8CB !important;
          }
          .ql-snow .ql-picker-label.ql-active .ql-stroke {
            stroke: #2Ba8CB !important;
          }
        `}
        </style>
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={editorValue}
          onChange={handleChange}
          modules={quillModules}
          formats={quillFormats}
          style={quillStyle}
          placeholder={placeholder}
        />
        <Modal
          className="[&_.ant-modal-content]:dark:bg-[#161616] [&_.ant-modal-header]:dark:bg-[#161616] [&_.ant-modal-header]:dark:border-gray-800 [&_.ant-modal-title]:dark:text-white [&_.ant-modal-close]:dark:text-gray-400 [&_.ant-modal-close:hover]:dark:text-white [&_.ant-modal-footer]:dark:bg-[#161616] [&_.ant-modal-footer]:dark:border-gray-800"
          title="Preview"
          open={previewVisible}
          onCancel={() => setPreviewVisible(false)}
          footer={[
            <button
              key="close"
              onClick={() => setPreviewVisible(false)}
              className="px-4 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#0E0E0E] text-gray-700 dark:text-gray-200 text-sm font-medium hover:bg-gray-50 dark:hover:bg-[#1F1F1F] transition-colors"
            >
              Close
            </button>
          ]}
          width="90%"
          style={{ maxWidth: '1200px' }}
        >
          <div
            dangerouslySetInnerHTML={{ __html: editorValue }}
            style={{
              padding: '20px',
              backgroundColor: isDarkMode ? '#0E0E0E' : '#ffffff',
              color: isDarkMode ? '#e4e4e7' : '#111827',
              borderRadius: '12px',
              minHeight: '200px'
            }}
            className="prose max-w-none dark:prose-invert"
          />
        </Modal>
      </div>
    </div>
  );
};

export default RichTextEditor;